import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { BlogPost } from '../../../mocks/blogPosts';

interface BlogGridProps {
  posts: BlogPost[];
}

const BlogGrid = ({ posts }: BlogGridProps) => {
  if (posts.length === 0) {
    return (
      <div className="py-16 md:py-24 text-center">
        <div className="w-12 h-12 md:w-16 md:h-16 flex items-center justify-center mx-auto mb-3 md:mb-4 bg-neutral-100 rounded-full">
          <i className="ri-search-line text-xl md:text-2xl text-neutral-400" />
        </div>
        <h3 className="text-base md:text-lg font-semibold text-neutral-700 mb-2">Nenhum artigo encontrado</h3>
        <p className="text-xs md:text-sm text-neutral-500">Tente buscar por outro termo ou categoria.</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-8">
      {posts.map((post, index) => (
        <motion.article
          key={post.id}
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: index * 0.1 }}
          className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer"
        >
          <Link to={`/blog/${post.slug}`} className="block">
            <div className="relative h-44 md:h-48 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
              />
              <div className="absolute top-3 left-3 md:top-4 md:left-4">
                <span
                  className={`${post.categoryColor} text-white text-xs font-semibold px-2.5 py-1 md:px-3 rounded-full`}
                >
                  {post.category}
                </span>
              </div>
            </div>

            <div className="p-5 md:p-6">
              <div className="flex items-center gap-3 md:gap-4 text-xs md:text-sm text-neutral-500 mb-2 md:mb-3">
                <div className="flex items-center gap-1">
                  <i className="ri-calendar-line text-[#D4AF37]" />
                  <span>{post.date}</span>
                </div>
                <div className="flex items-center gap-1">
                  <i className="ri-time-line text-[#D4AF37]" />
                  <span>{post.readTime}</span>
                </div>
              </div>

              <h3 className="text-lg md:text-xl font-bold text-neutral-900 mb-2 md:mb-3 group-hover:text-[#D4AF37] transition-colors line-clamp-2">
                {post.title}
              </h3>

              <p className="text-neutral-600 text-xs md:text-sm mb-3 md:mb-4 line-clamp-3">
                {post.excerpt}
              </p>

              <div className="flex flex-wrap gap-1.5 md:gap-2 mb-3 md:mb-4">
                {post.tags.slice(0, 3).map((tag) => (
                  <span
                    key={tag}
                    className="text-xs bg-neutral-100 text-neutral-600 px-2 py-1 rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>

              <div className="flex items-center gap-2 text-[#D4AF37] font-semibold text-xs md:text-sm group-hover:gap-3 transition-all">
                <span>Ler artigo completo</span>
                <i className="ri-arrow-right-line" />
              </div>
            </div>
          </Link>
        </motion.article>
      ))}
    </div>
  );
};

export default BlogGrid;